import { MatterCostContextMenu } from "@/components/features/matter/context-menus/MatterCostContextMenu";
import { DataTable, ServerErrorFallbackCard } from "@/components/ui";
import { useExpensesViewModel } from "@/hooks/matter-view-models/use-expenses-view-model";
import { useGlobalModal, useHandleSearchParams } from "@/hooks/useGlobalModal";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { Matter } from "@/lib/types";
import { getExpensePayeeConnection } from "@/lib/utils/connections/expense-contact-connections";
import { ErrorBoundary } from "@sentry/react";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { createMatterExpensesColumns } from "./matter-expenses-columns";

interface MatterExpensesTableProps {
  matter: Matter;
  tableButtons?: React.ReactNode;
  searchExpenseId?: string;
}

const MatterExpensesTableDangerous = ({
  matter,
  tableButtons,
  searchExpenseId,
}: MatterExpensesTableProps) => {
  const { openExpenseModal } = useGlobalModal();
  const { handleExpenseSearch } = useHandleSearchParams();
  const location = useLocation();

  const expensesViewModel = useExpensesViewModel(matter.id);

  const isOnSettlementPage = location.pathname.includes("/settlements");
  const columns = createMatterExpensesColumns(isOnSettlementPage);
  const processedSearches = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!searchExpenseId) return;

    const searchKey = `expense-${searchExpenseId}`;
    if (processedSearches.current.has(searchKey)) {
      return;
    }
    processedSearches.current.add(searchKey);

    handleExpenseSearch(matter, searchExpenseId);
  }, [searchExpenseId, matter, handleExpenseSearch]);

  return (
    <DataTable
      entityLabelPlural="costs"
      columns={columns}
      data={expensesViewModel.map((expense) => ({
        ...expense,
        matterId: matter.id,
        expensePayee: getExpensePayeeConnection(expense),
      }))}
      onRowClick={(row) => {
        openExpenseModal({
          matterId: matter.id,
          expenseId: row.original.id,
          editing: true,
        });
      }}
      renderRowContextMenu={(row, trigger) => (
        <MatterCostContextMenu
          expense={row.original}
          matter={matter}
          trigger={trigger}
        />
      )}
      tableName={tanstackTableNames.matterExpenses}
      tableButtons={tableButtons}
      initialSort={{
        id: "Date Created",
        desc: true,
      }}
    />
  );
};

export const MatterExpensesTable = (props: MatterExpensesTableProps) => (
  <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
    <MatterExpensesTableDangerous {...props} />
  </ErrorBoundary>
);
